import React from 'react'
import parse, { domToReact, HTMLReactParserOptions, Element, Text, DOMNode } from 'html-react-parser'
import { Typography, Table, TableBody, TableCell, TableHead, TableRow, ListItem, List } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import StyledLink from 'components/StyledLink'

export interface ISectionHtmlProps {
  source: string
  compact?: boolean
}

const useStyles = makeStyles<{ compact: boolean }>()((theme, { compact }) => ({
  heading: {
    margin: compact ? 0 : theme.spacing(6, 0, 4, 0),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(4, 0, 2, 0),
    },
    '&:first-child': {
      marginTop: 0,
    },
  },
  paragraph: {
    margin: compact ? 0 : theme.spacing(4, 0),
    [theme.breakpoints.down('sm')]: {
      margin: theme.spacing(2, 0),
    },
    '&:first-child': {
      marginTop: 0,
    },
  },
}))

function getHeadingVariant(name: string) {
  const level = parseInt(name.slice(1), 10)
  const newLevel = Math.max(3, Math.min(6, level + 2))

  return `h${newLevel}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

function HtmlRenderer(props: ISectionHtmlProps) {
  const { source, compact = false } = props
  const { classes } = useStyles({ compact })

  const getTextAlign = (attribs: { style?: string }) => {
    if (attribs?.style) {
      if (attribs.style.includes('text-align: right')) return 'right'
      if (attribs.style.includes('text-align: justify')) return 'justify'
      if (attribs.style.includes('text-align: center')) return 'center'
    }
    return 'left'
  }

  const options: HTMLReactParserOptions = {
    replace: (domNode) => {
      if (typeof domNode === 'object' && 'name' in domNode) {
        const element = domNode as Element
        const { name, attribs, children } = element

        switch (name) {
          case 'p':
            return (
              <Typography
                variant="body1"
                component="p"
                className={classes.paragraph}
                style={{ textAlign: getTextAlign(attribs) }}
              >
                {domToReact(children as DOMNode[], options)}
              </Typography>
            )
          case 'h1':
          case 'h2':
          case 'h3':
          case 'h4':
          case 'h5':
          case 'h6':
            return (
              <Typography
                variant={getHeadingVariant(name)}
                gutterBottom
                className={classes.heading}
              >
                {domToReact(children as DOMNode[], options)}
              </Typography>
            )
          case 'ol':
            return (
              <List sx={{ listStyle: 'decimal', pl: 2 }}>
                <Typography
                  variant="body1"
                  component="p"
                  className={classes.paragraph}
                >
                  {domToReact(children as DOMNode[], options)}
                </Typography>
              </List>
            )
          case 'ul':
            return (
              <List sx={{ listStyle: 'disc', pl: 2 }}>
                <Typography
                  variant="body1"
                  component="p"
                  className={classes.paragraph}
                >
                  {domToReact(children as DOMNode[], options)}
                </Typography>
              </List>
            )
          case 'li':
            return (
              <ListItem sx={{ display: 'list-item' }}>
                {domToReact(children as DOMNode[], options)}
              </ListItem>
            )
          case 'table':
            return (
              <div style={{ overflow: 'auto' }}>
                <Table>{domToReact(children as DOMNode[], options)}</Table>
              </div>
            )
          case 'thead':
            return <TableHead>{domToReact(children as DOMNode[], options)}</TableHead>
          case 'tbody':
            return <TableBody>{domToReact(children as DOMNode[], options)}</TableBody>
          case 'tr':
            return <TableRow>{domToReact(children as DOMNode[], options)}</TableRow>
          case 'th':
          case 'td':
            return <TableCell>{domToReact(children as DOMNode[], options)}</TableCell>
          case 'a':
            return (
              <StyledLink href={attribs.href}>
                {domToReact(children as DOMNode[], options)}
              </StyledLink>
            )
          case 'img':
            return <img src={attribs.src} alt={attribs.alt} style={{ maxWidth: '100%' }} />
          default:
            return domNode
        }
      }

      if (domNode instanceof Text) {
        return domNode.data.trim() ? <span>{domNode.data}</span> : null
      }

      return domNode
    },
  }

  return <React.Fragment>{parse(source, options)}</React.Fragment>
}

export default React.memo(HtmlRenderer)
